import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Plus, Trash2, Save, Download, Library, BookmarkPlus, FileSpreadsheet, Check, Loader2, Sparkles, X, Info, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const UNITS = ["item", "m", "m2", "m3", "ft", "hr", "each", "kg", "no."];

// Built-in quick-select UK trade materials with sensible default units.
const UK_MATERIALS = [
  { description: "Trench Block", unit: "m2" },
  { description: "Facing Bricks", unit: "m2" },
  { description: "Thermal Blocks", unit: "m2" },
  { description: "Cavity Insulation", unit: "m2" },
  { description: "DPC (Damp Proof Course)", unit: "m" },
  { description: "Screed", unit: "m2" },
  { description: "Blockwork", unit: "m2" },
  { description: "Plasterboard", unit: "m2" },
  { description: "Skim & decoration", unit: "m2" },
  { description: "Suspended ceiling", unit: "m2" },
  { description: "FD30 door sets", unit: "no." },
  { description: "Skirting / architrave", unit: "m" },
];

// Lightweight fuzzy score: substring match ranks highest, then subsequence match.
const fuzzyScore = (q, text) => {
  q = (q || "").toLowerCase().trim();
  const t = (text || "").toLowerCase();
  if (!q) return 0.01;
  const idx = t.indexOf(q);
  if (idx !== -1) return 2 - idx / 100;
  let ti = 0, matched = 0;
  for (let i = 0; i < q.length; i++) {
    const found = t.indexOf(q[i], ti);
    if (found === -1) return 0;
    ti = found + 1; matched++;
  }
  return matched / t.length;
};

// Description input with a fuzzy-search preset dropdown (fixed-positioned to escape the table's overflow).
const DescriptionCell = ({ value, presets, onChange, onSelect, onSavePreset, testId, inputClassName = "ledger-input" }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const inputRef = useRef(null);

  const place = () => {
    const r = inputRef.current?.getBoundingClientRect();
    if (r) setCoords({ left: r.left, top: r.bottom + 2, width: r.width });
  };
  const matches = useMemo(() => (
    presets
      .map((p) => ({ ...p, _s: fuzzyScore(value, p.description) }))
      .filter((p) => p._s > 0)
      .sort((a, b) => b._s - a._s)
      .slice(0, 6)
  ), [value, presets]);
  const exact = presets.some((p) => p.description.toLowerCase() === (value || "").toLowerCase().trim());
  const canSave = (value || "").trim().length > 1 && !exact;

  return (
    <>
      <input
        ref={inputRef}
        className={inputClassName}
        value={value}
        placeholder="Description of work"
        data-testid={testId}
        onChange={(e) => { onChange(e.target.value); place(); setOpen(true); }}
        onFocus={() => { place(); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && coords && (matches.length > 0 || canSave) && (
        <div
          className="fixed z-[70] bg-white border border-slate-200 rounded-md shadow-lg py-1 text-sm max-h-64 overflow-auto"
          style={{ left: coords.left, top: coords.top, width: Math.max(coords.width, 240) }}
          onMouseDown={(e) => e.preventDefault()}
          data-testid="description-autocomplete"
        >
          {matches.map((m, i) => (
            <button
              key={m.id || m.description}
              onClick={() => { onSelect(m); setOpen(false); }}
              data-testid={`description-suggestion-${i}`}
              className="w-full text-left px-3 py-1.5 hover:bg-blue-50 flex items-center justify-between gap-2"
            >
              <span className="text-slate-700">{m.description}</span>
              <span className="text-xs text-slate-400 shrink-0">{m.unit}{m.custom ? " · saved" : ""}</span>
            </button>
          ))}
          {canSave && (
            <button
              onClick={() => { onSavePreset((value || "").trim()); setOpen(false); }}
              data-testid="description-save-preset"
              className="w-full text-left px-3 py-1.5 text-blue-600 hover:bg-blue-50 border-t border-slate-100 flex items-center gap-1.5"
            >
              <BookmarkPlus className="h-3.5 w-3.5" /> Save “{(value || "").trim()}” as preset
            </button>
          )}
        </div>
      )}
    </>
  );
};

const rowTotal = (r) => (Number(r.qty) || 0) * (Number(r.rate) || 0) * (1 + (Number(r.markup) || 0) / 100);
const gbp = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EstimateMatrix = ({ projectId, takeoff }) => {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoState, setAutoState] = useState("idle");
  const [loaded, setLoaded] = useState(false);
  const [rates, setRates] = useState([]);
  const [libOpen, setLibOpen] = useState(false);
  const [newRate, setNewRate] = useState({ description: "", unit: "item", rate: "" });
  const [presets, setPresets] = useState([]);
  const [materialsOpen, setMaterialsOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/estimate`);
    setRows(data.line_items || []);
    setLoaded(true);
  }, [projectId]);
  const loadRates = useCallback(async () => {
    const { data } = await api.get(`/rates`);
    setRates(data);
  }, []);
  const [seeding, setSeeding] = useState(false);
  const [packs, setPacks] = useState([]);
  const [benchmarks, setBenchmarks] = useState({});
  const [regions, setRegions] = useState([]);
  const { tenant } = useAuth();
  const [region, setRegion] = useState(() => { try { return localStorage.getItem("bs_bench_region") || ""; } catch { return ""; } });
  const [selectedPack, setSelectedPack] = useState("general");
  const [editBench, setEditBench] = useState(null); // {key, low, high}
  const refetchBenchmarks = () => {
    const r = region || tenant?.region || "";
    api.get(`/rates/benchmarks${r ? `?region=${r}` : ""}`).then(({ data }) => setBenchmarks(data.benchmarks || {})).catch(() => {});
  };
  const saveBench = async () => {
    if (!editBench) return;
    try {
      await api.put(`/rates/benchmark-override`, { description: editBench.key, low: Number(editBench.low), high: Number(editBench.high) });
      setEditBench(null);
      refetchBenchmarks();
      toast.success("Range updated for your workspace");
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't save range"); }
  };
  const resetBench = async (key) => {
    try { await api.put(`/rates/benchmark-override`, { description: key, low: null, high: null }); refetchBenchmarks(); toast.success("Range reset to typical UK"); } catch {}
  };
  useEffect(() => {
    if (!libOpen) return;
    if (!packs.length) api.get(`/rates/starter-packs`).then(({ data }) => setPacks(data.packs || [])).catch(() => {});
    const r = region || tenant?.region || "";
    api.get(`/rates/benchmarks${r ? `?region=${r}` : ""}`).then(({ data }) => { setBenchmarks(data.benchmarks || {}); if (data.regions) setRegions(data.regions); if (!region && data.region) setRegion(data.region); }).catch(() => {});
  }, [libOpen, region]);
  useEffect(() => {
    const r = region || tenant?.region || "";
    api.get(`/rates/benchmarks${r ? `?region=${r}` : ""}`).then(({ data }) => { setBenchmarks(data.benchmarks || {}); if (data.regions) setRegions(data.regions); if (!region && data.region) setRegion(data.region); }).catch(() => {});
  }, [tenant?.region]);
  const benchFor = (r) => benchmarks[(r?.description || "").trim().toLowerCase()];
  const isOutOfRange = (r) => { const b = benchFor(r); return !!(b && Number(r.rate) > 0 && (Number(r.rate) < b.low || Number(r.rate) > b.high)); };
  const loadStarterRates = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post(`/rates/seed-starter`, { pack: selectedPack });
      await loadRates();
      const extra = data.skipped ? ` (${data.skipped} already in your library)` : "";
      if (data.added) toast.success(`Added ${data.added} ${data.label} rates${extra} — tweak them to your prices.`);
      else toast.info(`All ${data.label} rates are already in your library.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't load starter rates"); }
    finally { setSeeding(false); }
  };
  const loadPresets = useCallback(async () => {
    try { const { data } = await api.get(`/description-presets`); setPresets(data); }
    catch { /* non-fatal */ }
  }, []);
  useEffect(() => { load(); loadRates(); loadPresets(); }, [load, loadRates, loadPresets]);

  // Built-in UK materials + the user's saved custom presets, for the tag bar and autocomplete.
  const allPresets = useMemo(() => [
    ...UK_MATERIALS.map((m) => ({ ...m, custom: false })),
    ...presets.map((p) => ({ id: p.id, description: p.description, unit: p.unit, custom: true })),
  ], [presets]);

  const savePreset = async (description, unit = "item") => {
    try {
      const { data } = await api.post(`/description-presets`, { description, unit });
      setPresets((p) => (p.some((x) => x.id === data.id) ? p : [...p, data]));
      toast.success(`Saved “${description}” as a preset`);
    } catch { toast.error("Could not save preset"); }
  };
  const deletePreset = async (id) => {
    try { await api.delete(`/description-presets/${id}`); setPresets((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Could not remove preset"); }
  };

  useEffect(() => {
    if (!loaded) return;
    setAutoState("saving");
    const t = setTimeout(async () => {
      try { await api.put(`/projects/${projectId}/estimate`, { line_items: rows }); setAutoState("saved"); }
      catch { setAutoState("error"); }
    }, 1200);
    return () => clearTimeout(t);
  }, [rows, projectId, loaded]);

  const addRow = (preset = {}) =>
    setRows((r) => [...r, { id: crypto.randomUUID(), description: "", qty: 0, unit: "item", rate: 0, markup: 0, ...preset }]);

  const update = (id, key, val) => setRows((r) => r.map((x) => (x.id === id ? { ...x, [key]: val } : x)));
  const remove = (id) => setRows((r) => r.filter((x) => x.id !== id));

  const importTakeoff = () => {
    const agg = takeoff?.aggregated;
    if (!agg || (!agg.count_total && !agg.length_total && !agg.area_total)) { toast.info("No takeoff quantities to import yet"); return; }
    const preset = [];
    if (agg.count_total) preset.push({ id: crypto.randomUUID(), description: "Counted items (from takeoff)", qty: agg.count_total, unit: "no.", rate: 0, markup: 0 });
    if (agg.length_total) preset.push({ id: crypto.randomUUID(), description: "Measured runs (from takeoff)", qty: Number(agg.length_total), unit: takeoff?.scale?.["1"]?.unit || takeoff?.scale?.unit || "m", rate: 0, markup: 0 });
    if (agg.area_total) preset.push({ id: crypto.randomUUID(), description: "Measured areas (from takeoff)", qty: Number(agg.area_total), unit: "m2", rate: 0, markup: 0 });
    setRows((r) => [...r, ...preset]);
    toast.success("Imported takeoff quantities");
  };

  const addFromLibrary = (item) => {
    addRow({ description: item.description, unit: item.unit, rate: item.rate, qty: 1 });
    toast.success(`Added "${item.description}"`);
  };

  const saveRowToLibrary = async (row) => {
    if (!row.description) { toast.info("Add a description before saving to library"); return; }
    try {
      const { data } = await api.post(`/rates`, { description: row.description, unit: row.unit, rate: Number(row.rate) || 0 });
      setRates((r) => [...r, data]);
      toast.success("Saved to rate library");
    } catch { toast.error("Could not save rate"); }
  };

  const createRate = async () => {
    if (!newRate.description) return;
    try {
      const { data } = await api.post(`/rates`, { description: newRate.description, unit: newRate.unit, rate: Number(newRate.rate) || 0 });
      setRates((r) => [...r, data]);
      setNewRate({ description: "", unit: "item", rate: "" });
      toast.success("Rate added to library");
    } catch { toast.error("Could not add rate"); }
  };

  const deleteRate = async (id) => {
    await api.delete(`/rates/${id}`);
    setRates((r) => r.filter((x) => x.id !== id));
  };

  const save = async () => {
    setSaving(true);
    try { await api.put(`/projects/${projectId}/estimate`, { line_items: rows }); setAutoState("saved"); toast.success("Estimate saved"); }
    catch { setAutoState("error"); toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const exportCsv = async () => {
    try {
      await api.put(`/projects/${projectId}/estimate`, { line_items: rows });
      const res = await api.get(`/projects/${projectId}/estimate/csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "estimate.csv";
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Estimate exported to CSV");
    } catch { toast.error("CSV export failed"); }
  };

  const [aiRating, setAiRating] = useState(false);
  const aiSuggestRates = async () => {
    if (!rows.length) { toast.error("Add some estimate lines first"); return; }
    setAiRating(true);
    try {
      await api.put(`/projects/${projectId}/estimate`, { line_items: rows });
      const { data } = await api.post(`/projects/${projectId}/ai/suggest-rates`);
      const applied = (data.suggestions || []).filter((s) => s.suggested_rate != null && s.confidence >= 0.4);
      if (!applied.length) { toast.info("No confident rate matches found in your library"); return; }
      setRows((prev) => prev.map((r, i) => {
        const s = applied.find((x) => x.index === i);
        return s && (!r.rate || Number(r.rate) === 0) ? { ...r, rate: s.suggested_rate } : r;
      }));
      toast.success(`AI suggested rates for ${applied.length} line${applied.length === 1 ? "" : "s"} — please review`);
    } catch (err) { toast.error(err.response?.data?.detail || "Rate suggestions failed"); }
    finally { setAiRating(false); }
  };

  const grand = rows.reduce((s, r) => s + rowTotal(r), 0);
  const regionLabel = (regions.find((x) => x.key === (region || tenant?.region)) || {}).label || "UK national";
  const outOfRangeCount = rows.filter(isOutOfRange).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => addRow()} data-testid="add-estimate-row" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> Add line</Button>
          <Button size="sm" variant="outline" onClick={importTakeoff} data-testid="import-takeoff-btn" className="border-slate-300"><Download className="h-4 w-4 mr-1.5" /> Import from takeoff</Button>
          <Button size="sm" variant="outline" onClick={() => setLibOpen(true)} data-testid="rate-library-btn" className="border-slate-300"><Library className="h-4 w-4 mr-1.5" /> Rate library</Button>
          <Button size="sm" variant="outline" onClick={aiSuggestRates} disabled={aiRating} data-testid="ai-rates-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50">
            {aiRating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} AI suggest rates
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="estimate-autosave-status">
            {autoState === "saving" && <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>}
            {autoState === "saved" && <><Check className="h-3.5 w-3.5 text-green-600" /> All changes saved</>}
            {autoState === "error" && <button onClick={save} className="text-red-500 flex items-center gap-1.5" data-testid="estimate-autosave-retry"><Save className="h-3.5 w-3.5" /> Save failed — retry</button>}
          </span>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-csv-btn" className="border-slate-300"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="save-estimate-btn" className="bg-slate-900 hover:bg-slate-800"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save estimate"}</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-500" data-testid="estimate-region-label">Pricing region: <span className="font-medium text-slate-700">{regionLabel}</span></span>
        {outOfRangeCount > 0 && (
          <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1" data-testid="benchmark-insights-banner">
            <Info className="h-3.5 w-3.5" />
            {outOfRangeCount} line{outOfRangeCount === 1 ? "" : "s"} priced outside your typical range — worth a check before you send
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="materials-tag-bar">
        {!materialsOpen ? (
          <button
            onClick={() => setMaterialsOpen(true)}
            data-testid="materials-toggle-open"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 rounded-full px-3 py-1 border border-dashed border-slate-300 hover:bg-slate-50 hover:text-slate-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Add material
          </button>
        ) : (
          <>
            <button
              onClick={() => setMaterialsOpen(false)}
              data-testid="materials-toggle-close"
              className="text-xs text-slate-400 self-center mr-1 inline-flex items-center gap-1 hover:text-slate-600"
              title="Hide materials"
            >
              Materials: <X className="h-3 w-3" />
            </button>
            {allPresets.map((m) => (
          <span key={m.id || m.description} className="group inline-flex items-center">
            <button
              onClick={() => { addRow({ description: m.description, unit: m.unit, qty: 1 }); window.dispatchEvent(new Event("tradeoff:estimate-line-added")); toast.success(`Added “${m.description}”`); }}
              disabled={!loaded}
              data-testid={`material-tag-${(m.id || m.description).toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
              className={`text-xs rounded-full px-3 py-1 border transition-colors disabled:opacity-50 ${m.custom ? "bg-violet-50 text-violet-700 border-violet-100 hover:bg-violet-100" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"} ${m.custom ? "rounded-r-none" : ""}`}
            >
              {m.description} <span className="text-slate-400">· {m.unit}</span>
            </button>
            {m.custom && (
              <button
                onClick={() => deletePreset(m.id)}
                data-testid={`material-tag-remove-${m.id}`}
                title="Remove preset"
                className="text-xs bg-violet-50 text-violet-400 hover:text-red-500 border border-l-0 border-violet-100 rounded-full rounded-l-none pr-2 pl-1 py-1"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
          </>
        )}
      </div>

      {rates.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="rate-quick-add">
          <span className="text-xs text-slate-400 self-center mr-1">Quick add:</span>
          {rates.slice(0, 8).map((it) => (
            <button key={it.id} onClick={() => addFromLibrary(it)} data-testid={`rate-chip-${it.id}`}
              className="text-xs bg-blue-50 text-blue-700 border border-blue-100 rounded-full px-3 py-1 hover:bg-blue-100 transition-colors">
              {it.description} · {gbp(it.rate)}/{it.unit}
            </button>
          ))}
        </div>
      )}

      <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
        <table className="w-full ledger-table" data-testid="estimate-table">
          <thead className="bg-slate-50">
            <tr className="text-xs uppercase tracking-wide text-slate-500 text-left">
              <th className="w-[34%]">Description</th>
              <th className="w-20 text-right">Qty</th>
              <th className="w-24">Unit</th>
              <th className="w-28 text-right">Rate (£)</th>
              <th className="w-24 text-right">Markup %</th>
              <th className="w-32 text-right">Total</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="py-10" data-testid="estimate-empty-state">
                <div className="text-center">
                  <Library className="h-8 w-8 text-slate-300 mx-auto" />
                  <p className="mt-2 text-slate-500 text-sm">No line items yet — build your estimate.</p>
                  <div className="mt-3 flex items-center justify-center gap-2">
                    <Button size="sm" onClick={() => addRow()} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> Add line</Button>
                    <Button size="sm" variant="outline" onClick={importTakeoff} className="border-slate-300"><Download className="h-4 w-4 mr-1.5" /> Import from takeoff</Button>
                  </div>
                </div>
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`estimate-row-${r.id}`}>
                <td><DescriptionCell
                  value={r.description}
                  presets={allPresets}
                  testId={`estimate-desc-${r.id}`}
                  onChange={(v) => update(r.id, "description", v)}
                  onSelect={(m) => setRows((rows) => rows.map((x) => (x.id === r.id ? { ...x, description: m.description, unit: m.unit } : x)))}
                  onSavePreset={(text) => savePreset(text, r.unit)}
                /></td>
                <td><input type="number" className="ledger-input text-right font-mono" value={r.qty} onChange={(e) => update(r.id, "qty", e.target.value)} /></td>
                <td>
                  <select className="ledger-input" value={r.unit} onChange={(e) => update(r.id, "unit", e.target.value)}>
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </td>
                <td><div className="relative flex items-center">
                  <input type="number" className={`ledger-input text-right font-mono ${isOutOfRange(r) ? "text-amber-700" : ""}`} value={r.rate} onChange={(e) => update(r.id, "rate", e.target.value)} />
                  {isOutOfRange(r) && (() => { const b = benchFor(r); return <span className="ml-1 text-amber-500" data-testid={`rate-flag-${r.id}`} title={`Outside your typical range (${gbp(b.low)}–${gbp(b.high)}/${b.unit})`}><Info className="h-3.5 w-3.5" /></span>; })()}
                </div></td>
                <td><input type="number" className="ledger-input text-right font-mono" value={r.markup} onChange={(e) => update(r.id, "markup", e.target.value)} /></td>
                <td className="text-right font-mono font-semibold text-navy">{gbp(rowTotal(r))}</td>
                <td className="text-center whitespace-nowrap">
                  <button onClick={() => saveRowToLibrary(r)} className="text-slate-300 hover:text-blue-600 mr-1.5" title="Save to library" data-testid={`save-rate-${r.id}`}><BookmarkPlus className="h-4 w-4 inline" /></button>
                  <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500" data-testid={`remove-estimate-${r.id}`}><Trash2 className="h-4 w-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 font-semibold">
              <td colSpan={5} className="text-right text-slate-600">Estimate total</td>
              <td className="text-right font-mono text-lg text-blue-700" data-testid="estimate-grand-total">{gbp(grand)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent data-testid="rate-library-dialog">
          <DialogHeader>
            <DialogTitle>Rate library</DialogTitle>
            <DialogDescription>Save your common rates once and reuse them across every project.</DialogDescription>
          </DialogHeader>
          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50/50 p-3" data-testid="starter-pack-picker">
            <label className="text-xs font-medium text-slate-600">Load a starter pack for your trade</label>
            <div className="mt-1.5 flex gap-2">
              <select value={selectedPack} onChange={(e) => setSelectedPack(e.target.value)} data-testid="starter-pack-select" className="h-9 flex-1 border border-slate-300 rounded-md text-sm px-2 bg-white">
                {packs.map((p) => <option key={p.key} value={p.key}>{p.label} ({p.count})</option>)}
              </select>
              <Button size="sm" onClick={loadStarterRates} disabled={seeding || !packs.length} data-testid="load-starter-rates-btn" className="bg-blue-600 hover:bg-blue-700 h-9 shrink-0">
                {seeding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Load pack
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">Indicative UK rates you can edit to match your prices. Duplicates are skipped.</p>
          </div>
          <div className="space-y-2 max-h-64 overflow-auto">
            <div className="flex items-center justify-between gap-2 pb-1" data-testid="benchmark-region-row">
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                Based on typical UK rates
                <Info className="h-3 w-3 text-slate-400 cursor-help" data-testid="benchmark-info"
                  title="Indicative ranges built from common UK subcontractor prices (roughly ±20% around a typical rate), adjusted for your selected region. They're a sanity check, not a quote — always edit each rate to your own price." />
              </span>
              <select
                value={region}
                onChange={(e) => { setRegion(e.target.value); try { localStorage.setItem("bs_bench_region", e.target.value); } catch {} }}
                data-testid="benchmark-region-select"
                className="h-7 border border-slate-200 rounded-md text-[11px] px-1.5 bg-white text-slate-600"
              >
                {(regions.length ? regions : [{ key: "national", label: "UK national" }]).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            {rates.length === 0 && <p className="text-sm text-slate-400 text-center py-4" data-testid="rate-library-empty">No saved rates yet — load a starter pack above or add your own below.</p>}
            {rates.map((it) => {
              const bm = benchmarks[(it.description || "").trim().toLowerCase()];
              const outOfRange = bm && (Number(it.rate) < bm.low || Number(it.rate) > bm.high);
              return (
              <div key={it.id} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2" data-testid={`rate-item-${it.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{it.description}</p>
                  <p className="text-xs text-slate-400 font-mono">{gbp(it.rate)} / {it.unit}</p>
                  {bm && (() => {
                    const bkey = (it.description || "").trim().toLowerCase();
                    if (editBench && editBench.key === bkey) {
                      return (
                        <div className="mt-1 flex items-center gap-1" data-testid={`rate-benchmark-edit-${it.id}`}>
                          <span className="text-[11px] text-slate-400">£</span>
                          <input type="number" value={editBench.low} onChange={(e) => setEditBench({ ...editBench, low: e.target.value })} className="h-6 w-16 border border-slate-300 rounded text-[11px] px-1" data-testid={`bench-low-${it.id}`} />
                          <span className="text-[11px] text-slate-400">–£</span>
                          <input type="number" value={editBench.high} onChange={(e) => setEditBench({ ...editBench, high: e.target.value })} className="h-6 w-16 border border-slate-300 rounded text-[11px] px-1" data-testid={`bench-high-${it.id}`} />
                          <button onClick={saveBench} data-testid={`bench-save-${it.id}`} className="text-[11px] text-blue-600 font-medium px-1">Save</button>
                          <button onClick={() => setEditBench(null)} className="text-[11px] text-slate-400 px-0.5">Cancel</button>
                        </div>
                      );
                    }
                    return (
                      <p className={`text-[11px] mt-0.5 flex items-center gap-1 ${outOfRange ? "text-amber-600" : "text-slate-400"}`} data-testid={`rate-benchmark-${it.id}`}>
                        <span>{bm.edited ? "Your range" : "Indicative UK"}: {gbp(bm.low)}–{gbp(bm.high)}/{bm.unit}{outOfRange ? " · outside range" : ""}</span>
                        <button onClick={() => setEditBench({ key: bkey, low: bm.low, high: bm.high })} className="text-slate-300 hover:text-blue-600" data-testid={`bench-edit-${it.id}`} title="Tune this range for your workspace"><Pencil className="h-3 w-3" /></button>
                        {bm.edited && <button onClick={() => resetBench(bkey)} className="text-slate-300 hover:text-slate-500" title="Reset to typical UK">reset</button>}
                      </p>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => addFromLibrary(it)} className="border-slate-300 h-8">Add</Button>
                  <button onClick={() => deleteRate(it.id)} className="text-slate-300 hover:text-red-500" data-testid={`delete-rate-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              );
            })}
          </div>
          <div className="border-t border-slate-200 pt-4 grid grid-cols-12 gap-2 items-end">
            <div className="col-span-6"><label className="text-xs text-slate-500">Description</label><Input value={newRate.description} onChange={(e) => setNewRate({ ...newRate, description: e.target.value })} data-testid="new-rate-desc" className="h-9" /></div>
            <div className="col-span-3">
              <label className="text-xs text-slate-500">Unit</label>
              <select value={newRate.unit} onChange={(e) => setNewRate({ ...newRate, unit: e.target.value })} className="h-9 w-full border border-slate-300 rounded-md text-sm px-2" data-testid="new-rate-unit">
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="col-span-3"><label className="text-xs text-slate-500">Rate £</label><Input type="number" value={newRate.rate} onChange={(e) => setNewRate({ ...newRate, rate: e.target.value })} data-testid="new-rate-value" className="h-9 font-mono" /></div>
          </div>
          <Button onClick={createRate} disabled={!newRate.description} data-testid="add-rate-btn" className="bg-blue-600 hover:bg-blue-700 mt-2"><Plus className="h-4 w-4 mr-1.5" /> Add to library</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstimateMatrix;
